import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { IEmailAddressControlProps } from '../../components/userDataManagement/EmailAdddressControl';
import { EmailAddressControl } from '../../components/userDataManagement/EmailAdddressControl';

interface useEmailAddressControlComponentProps {
    emailAddress: string | undefined | null;
    handleChangeCustomControl: <T = any>(values: Partial<T>) => Promise<void>;
    validateOneCustomControl: (name: string) => Promise<void>;
}

export function useEmailAddressControlComponent(options: useEmailAddressControlComponentProps) {
    const { emailAddress: originalEmailAddress, handleChangeCustomControl, validateOneCustomControl } = options;
    const [isEmailKnown] = useState(!isEmpty(originalEmailAddress));
    const [isSubmitAllowed, setIsSubmitAllowed] = useState(false);
    const [showCheckbox, setShowCheckbox] = useState(false);
    const [showCheckboxWarning, setshowCheckboxWarning] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const [emailAddress, setEmailAddress] = useState(originalEmailAddress);
    const [isEmailEmpty, setIsEmailEmpty] = useState(isEmpty(emailAddress));

    const validateEmailAddress = useCallback(() => {
        if (isSubmitAllowed) {
            return true;
        }
        if (showCheckbox && !isChecked) {
            setshowCheckboxWarning(true);
        } else if (isEmailEmpty) {
            setShowCheckbox(true);
        }

        return false;
    }, [isEmailEmpty, isSubmitAllowed, showCheckbox, isChecked]);

    const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailAddress(event.target.value);
        handleChangeCustomControl({ emailadres: event.target.value ? event.target.value : null });
    }, []);

    const EmailAddressControlProps = useMemo(
        (): Omit<IEmailAddressControlProps, 'error'> => ({
            value: emailAddress,
            isChecked,
            isEmailKnown,
            onChange: onChange,
            onCheck: () => setIsChecked(!isChecked),
            onDoValidate: () => validateOneCustomControl('emailadres'),
            showCheckbox,
            showCheckboxWarning,
        }),
        [emailAddress, isChecked, onChange, showCheckbox, showCheckboxWarning]
    );

    useEffect(() => {
        if (originalEmailAddress) {
            handleChangeCustomControl({ emailadres: originalEmailAddress });
        }
    }, [originalEmailAddress]);

    useEffect(() => {
        setIsEmailEmpty(isEmpty(emailAddress));
    }, [emailAddress]);

    useEffect(() => {
        if (!isEmailEmpty) {
            setshowCheckboxWarning(false);
            setShowCheckbox(false);
            setIsChecked(false);
        }
    }, [isEmailEmpty]);

    useEffect(() => {
        setIsSubmitAllowed(!isEmpty(emailAddress) || (showCheckbox && isChecked));
    }, [emailAddress, showCheckbox, isChecked]);

    return {
        EmailAddressControlProps,
        validateEmailAddress,
        EmailAddressControl,
    };
}
